// ============ PARLOV GAP — the daily table ============
//
// The study below this block is 31 years of history with a fixed processing
// date. This is the same study pointed at the next two days: who reports, when
// the gap will actually open, and what the research expects on that day.
//
// The one thing this table refuses to blur: only ~40 of the study's 515
// symbols have published per-symbol statistics. A name either carries its own
// numbers or it does not, and the two are shown as two tables rather than one
// table with blanks — because a blank cell reads as "no gap risk" when it
// actually means "this study never singled this company out".

import React, { useEffect, useState } from 'react';
import { SecHead, Verdict } from './core';
import { fetchFundamentals, type Fundamental } from '../services/api';
import { useT } from '../lib/i18n';

interface Row {
  symbol: string; date: string; sess: 'bmo' | 'amc'; when: string; reported: boolean;
  est: string; last: string; revEst: string; revEstNum: number | null;
  gapDate: string | null; gapWeekday: string | null;
  weekdayAvgGap: number | null; weekdayN: number | null; notable: boolean;
  study: { n: number | null; avgAbnGap: number | null; maxGap: number | null; nofillRate: number | null; lists: string[] } | null;
}
interface Session { date: string; weekday: string | null; total: number; covered: number; rows: Row[] }
interface Daily {
  generated: string | null; stale: boolean; gapFloorPct: number | null;
  window: { from: string; to: string }; totalScheduled: number;
  defaultGapDate: string | null; sessions: Session[];
  overall: {
    fillRate: number | null; nofillRate: number | null;
    weekday: { day: string; n: number; avg_abn_gap: number }[];
    buydip: { bucket: string; n: number; avg_2wk: number; winrate_pct: number }[];
    gapdown2wk: { n?: number; avg_pct?: number; winrate_pct?: number } | null;
    gapup2wk: { n?: number; avg_pct?: number; winrate_pct?: number } | null;
  };
}

/** Our own per-symbol history, computed over every daily bar Yahoo has. */
interface GapStat {
  bars: number; from: string | null; to: string | null; floor: number;
  n: number; avgAbnGap: number | null; maxGap: number | null; maxAt: string | null;
  up: number; down: number; fillRate: number | null; nofillRate: number | null;
  error?: string;
}

interface ScorePart { k: string; th: string; en: string; v: number }
interface Screen {
  symbol: string;
  verdict: 'buy' | 'sell' | 'neutral';
  technical?: { score: number; parts: ScorePart[] } | null;
  fundamental?: { score: number; period?: string | null; parts: ScorePart[] } | null;
}

const pct = (v?: number | null, d = 2) => (typeof v === 'number' ? `${v.toFixed(d)}%` : '—');
const num = (v?: number | null, d = 2) => (typeof v === 'number' ? v.toLocaleString(undefined, { maximumFractionDigits: d }) : '—');
const LIST_TH: Record<string, string> = { biggest: 'gap เฉลี่ยสูงสุด', robust: 'ยืนยันซ้ำ (robust)', nofill: 'ไม่ปิด gap' };
const LIST_EN: Record<string, string> = { biggest: 'biggest avg gap', robust: 'robust', nofill: 'no-fill' };

/**
 * `/api/screen` fans out to Yahoo per symbol and caps a single request at 20.
 * The daily table can carry more than that, so the request is chunked and the
 * chunks run one after another — the point of the server's cap is to bound how
 * many Yahoo calls one request triggers, and firing all the chunks at once
 * would defeat it. Per-symbol results are cached server-side, so a second
 * viewer pays almost nothing.
 */
async function screenAll(symbols: string[]): Promise<Record<string, Screen>> {
  const out: Record<string, Screen> = {};
  for (let i = 0; i < symbols.length; i += 20) {
    const chunk = symbols.slice(i, i + 20);
    try {
      const r = await fetch(`/api/screen?symbols=${encodeURIComponent(chunk.join(','))}`);
      if (r.ok) Object.assign(out, await r.json());
    } catch { /* a chunk that fails leaves those rows without a read, not the table without rows */ }
  }
  return out;
}

/**
 * Gap history, chunked the same way. Each symbol is a full daily history
 * download server-side, cached for a day, so this is slow exactly once.
 */
async function gapsAll(symbols: string[], onChunk: (v: Record<string, GapStat>) => void) {
  for (let i = 0; i < symbols.length; i += 20) {
    const chunk = symbols.slice(i, i + 20);
    try {
      const r = await fetch(`/api/gap/stats?symbols=${encodeURIComponent(chunk.join(','))}`);
      // Painted per chunk rather than at the end: the first twenty rows should
      // not wait on the last twenty histories to download.
      if (r.ok) onChunk(await r.json());
    } catch { /* ignore — those rows keep their dash */ }
  }
}

export function ParlovDailyTable() {
  const t = useT();
  const [d, setD] = useState<Daily | null>(null);
  const [failed, setFailed] = useState(false);
  const [funds, setFunds] = useState<Record<string, Fundamental>>({});
  const [screen, setScreen] = useState<Record<string, Screen>>({});
  // Distinguishes "still loading" from "asked and Yahoo had nothing" — an
  // earnings calendar is full of tickers no screen can price, and a permanent
  // spinner in those rows would look like the table is broken.
  const [screened, setScreened] = useState(false);
  const [gaps, setGaps] = useState<Record<string, GapStat>>({});
  const [gapsDone, setGapsDone] = useState(false);
  const [how, setHow] = useState(false);
  /** Click-to-sort on the realised gap: biggest up, biggest down, or off. */
  const [gapSort, setGapSort] = useState<'desc' | 'asc' | null>(null);
  /** Live price move against yesterday's close, for the session happening now. */
  const [nowChg, setNowChg] = useState<Record<string, number>>({});
  /** The gap that actually printed, per symbol, for the selected session. */
  const [live, setLive] = useState<Record<string, { gapPct: number } | null>>({});
  /** Which gap session the table is showing; defaults to the next one. */
  const [gapDate, setGapDate] = useState<string | null>(null);
  /**
   * A session runs to a few hundred reports. Sixty is what fits a screen and
   * still reaches most of the names anyone is waiting for; asking for more is
   * a deliberate click, because every extra row is another screen to run.
   */
  const [limit, setLimit] = useState(60);

  useEffect(() => {
    let alive = true;
    setScreened(false); setGapsDone(false);
    let retry: ReturnType<typeof setTimeout> | undefined;
    const load = () => fetch(`/api/parlov/daily?limit=${limit}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(v => {
        if (!alive) return;
        setD(v); setFailed(false); setGapDate(g => g ?? v.defaultGapDate);
        // An empty calendar is upstream having a bad moment, and the server
        // only holds that result for a minute — so come back and ask again
        // rather than leaving the reader on a message forever.
        if (!v.sessions?.length) retry = setTimeout(load, 60000);
      })
      .catch(() => { if (alive) { setFailed(true); retry = setTimeout(load, 60000); } });
    load();
    return () => { alive = false; if (retry) clearTimeout(retry); };
  }, [limit]);

  // The same two calls the study's own table makes: trailing price/PE for the
  // columns, and the technical + latest-quarter screen for the verdict.
  //
  // Only the session on screen is screened. Screening all five days at once
  // would be three hundred symbols — fifteen chunked screen requests and
  // fifteen history requests before the first row could show a verdict. Both
  // are cached per symbol on the server, so switching sessions is slow once
  // and instant afterwards, and the days nobody opens cost nothing.
  useEffect(() => {
    if (!d) return;
    const sess = d.sessions.find(s => s.date === gapDate) || d.sessions[0];
    const syms = [...new Set((sess?.rows || []).map(r => r.symbol))];
    if (!syms.length) { setScreened(true); return; }
    let alive = true;
    fetchFundamentals(syms).then(f => { if (alive) setFunds(x => ({ ...x, ...f })); }).catch(() => {});
    setScreened(false); setGapsDone(false);
    screenAll(syms).then(v => { if (alive) { setScreen(x => ({ ...x, ...v })); setScreened(true); } });
    gapsAll(syms, v => { if (alive) setGaps(x => ({ ...x, ...v })); }).then(() => { if (alive) setGapsDone(true); });
    return () => { alive = false; };
  }, [d, gapDate]);

  /**
   * The gap that actually printed, polled for the session on screen.
   *
   * An opening gap is fixed the moment the session opens, so this is not a
   * ticking number — but it appears the instant the bell goes, and it belongs
   * to whichever session the reader has selected, so it re-runs on both. The
   * poll stops while the tab is hidden; nobody needs an open they cannot see.
   */
  useEffect(() => {
    const sess = d?.sessions.find(s => s.date === gapDate) || d?.sessions[0];
    if (!sess?.rows.length) return;
    let alive = true;
    const syms = sess.rows.map(r => r.symbol);
    const run = async () => {
      for (let i = 0; i < syms.length; i += 20) {
        const chunk = syms.slice(i, i + 20);
        try {
          const r = await fetch(`/api/gap/today?symbols=${encodeURIComponent(chunk.join(','))}&date=${sess.date}`);
          if (r.ok && alive) { const v = await r.json(); setLive(x => ({ ...x, ...v })); }
        } catch { /* the cell simply stays empty */ }
      }
    };
    run();
    // The visibility guard belongs on the REPEAT, never on the first fetch: a
    // background tab still has to have the number ready for the moment someone
    // looks at it, and `document.hidden` is true more often than it feels —
    // any tab that is not the front one reports hidden.
    const id = setInterval(() => { if (!document.hidden) run(); }, 60000);
    const onShow = () => { if (!document.hidden) run(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { alive = false; clearInterval(id); document.removeEventListener('visibilitychange', onShow); };
  }, [d, gapDate]);

  /**
   * The live line under the opening gap: where price stands against yesterday's
   * close, right now.
   *
   * The opening gap itself cannot tick — it is fixed the moment the session
   * opens, and that is the number the study is about. This is the one that
   * moves, so it is shown beneath rather than instead.
   *
   * Only for the session happening TODAY. On Monday's session viewed on
   * Tuesday a live quote would be a number about Tuesday sitting in a row
   * about Monday, which is worse than showing nothing.
   */
  useEffect(() => {
    const sess = d?.sessions.find(s => s.date === gapDate) || d?.sessions[0];
    if (!sess?.rows.length) return;
    if (sess.date !== new Date().toISOString().slice(0, 10)) { setNowChg({}); return; }
    let alive = true;
    const syms = sess.rows.map(r => r.symbol);
    const run = async () => {
      for (let i = 0; i < syms.length; i += 20) {
        const chunk = syms.slice(i, i + 20);
        try {
          // /api/yquotes returns chg already computed against the previous
          // close, which is exactly the figure wanted here.
          const r = await fetch(`/api/yquotes?symbols=${encodeURIComponent(chunk.join(','))}`);
          if (!r.ok || !alive) continue;
          const v = await r.json();
          const patch: Record<string, number> = {};
          for (const [sym, q] of Object.entries(v as Record<string, { chg?: number }>)) {
            if (typeof q?.chg === 'number') patch[sym] = q.chg;
          }
          if (alive && Object.keys(patch).length) setNowChg(x => ({ ...x, ...patch }));
        } catch { /* the line simply stays absent */ }
      }
    };
    run();
    const id = setInterval(() => { if (!document.hidden) run(); }, 20000);
    const onShow = () => { if (!document.hidden) run(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { alive = false; clearInterval(id); document.removeEventListener('visibilitychange', onShow); };
  }, [d, gapDate]);

  /**
   * Never disappear silently.
   *
   * This block used to return null the moment anything went wrong, so a failed
   * fetch or an empty calendar looked exactly like a feature that had been
   * removed — which is precisely how it was reported: "the daily table isn't
   * showing". Whatever the state, the card says which state it is in.
   */
  if (failed) return (
    <div className="mm-card glass mm-pd">
      <SecHead th="Parlov Gap รายวัน" en="PARLOV GAP DAILY"></SecHead>
      <p className="dim" style={{ margin: 0, fontSize: '0.72rem' }}>{t(
        'ดึงปฏิทินงบไม่สำเร็จ — ลองรีเฟรชอีกครั้งในสักครู่',
        'Could not load the earnings calendar — try refreshing in a moment.')}</p>
    </div>
  );
  if (!d) return <p className="dim" style={{ margin: '8px 0' }}>{t('กำลังโหลดปฏิทินงบวันนี้…', "Loading today's earnings calendar…")}</p>;
  if (!d.sessions.length) return (
    <div className="mm-card glass mm-pd">
      <SecHead th="Parlov Gap รายวัน" en="PARLOV GAP DAILY"
        right={<span className="mono dim">{d.window.from} → {d.window.to}</span>}></SecHead>
      <p className="dim" style={{ margin: 0, fontSize: '0.72rem' }}>{t(
        'ยังไม่ได้รายชื่อหุ้นที่ประกาศงบในช่วงนี้จากต้นทาง — ระบบจะลองใหม่เองภายในหนึ่งนาที',
        'The calendar returned no companies for this window — it retries on its own within a minute.')}</p>
    </div>
  );

  const wdBest = [...(d.overall.weekday || [])].sort((a, b) => b.avg_abn_gap - a.avg_abn_gap)[0];

  const session = d.sessions.find(s => s.date === gapDate) || d.sessions[0] || null;
  const rows = session?.rows || [];

  /**
   * Order within the table: the two priority tiers keep the order the server
   * gave them, and everything after the watched names sorts by the latest
   * quarter, best first.
   *
   * This reorders what is on screen; it does not change what got on screen.
   * The fundamental score comes from `/api/screen`, which runs in the browser
   * after the table has already been chosen, so the server still selects rows
   * by size — sorting here is the only place the score exists. Names with no
   * filing sort last rather than counting as zero: no filing is an absence,
   * not a neutral quarter.
   */
  // Not a hook: this sits below the component's early returns, and a hook here
  // would be a conditional one — React counts them and throws when the count
  // changes between renders. Sorting a hundred and fifty rows costs nothing.
  const ordered = (() => {
    const tier = (r: Row) => (r.study ? 0 : r.notable ? 1 : 2);
    const score = (r: Row) => screen[r.symbol]?.fundamental?.score;
    const gap = (r: Row) => live[r.symbol]?.gapPct;

    // An explicit sort means the reader wants the whole table by that number,
    // so the priority tiers step aside — pinning watched names to the top
    // would quietly contradict the column they just clicked.
    if (gapSort) {
      return [...rows].sort((a, b) => {
        const ga = gap(a), gb = gap(b);
        if (ga == null && gb == null) return 0;
        if (ga == null) return 1;   // no gap yet sorts last either way
        if (gb == null) return -1;
        return gapSort === 'desc' ? gb - ga : ga - gb;
      });
    }

    return [...rows].sort((a, b) => {
      if (tier(a) !== tier(b)) return tier(a) - tier(b);
      if (tier(a) < 2) return 0; // Array#sort is stable — keeps the server's order
      const fa = score(a), fb = score(b);
      if (fa == null && fb == null) return 0;
      if (fa == null) return 1;
      if (fb == null) return -1;
      return fb - fa;
    });
  })();

  const side = (parts?: ScorePart[]) => (parts || []).map(x => t(x.th, x.en)).join(' · ');
  /** Waiting on the request, versus asked and there was nothing to show. */
  const pending = <span className="dim mono">{screened ? '—' : '…'}</span>;

  /**
   * The same five analysis columns the study's own table carries, so a reader
   * moving between the two tables is reading one thing, not two.
   */
  const AnalysisCells = ({ sym }: { sym: string }) => {
    const f = funds[sym];
    const sc = screen[sym];
    return (
      <React.Fragment>
        <td className="r">{num(f?.price)}</td>
        <td className="r">{num(f?.pe, 1)}</td>
        <td className="mm-pd-cell">
          {sc?.fundamental
            ? <React.Fragment>
                <b className="mono">{sc.fundamental.period || ''}</b>
                <em className="dim">{side(sc.fundamental.parts)}</em>
              </React.Fragment>
            : (sc ? <span className="dim mono">{t('ไม่มีงบ', 'no filing')}</span> : pending)}
        </td>
        <td className="mm-pd-cell">
          {sc?.technical ? <em className="dim">{side(sc.technical.parts)}</em> : pending}
        </td>
        <td>
          {sc
            ? <span className="mm-pd-read">
                <Verdict v={sc.verdict}></Verdict>
                <em className="mono dim">
                  {t('เทคนิค', 'tech')} {sc.technical ? (sc.technical.score >= 0 ? '+' : '') + sc.technical.score : '—'}
                  {' · '}{t('งบ', 'fund')} {sc.fundamental ? (sc.fundamental.score >= 0 ? '+' : '') + sc.fundamental.score : '—'}
                </em>
              </span>
            : pending}
        </td>
      </React.Fragment>
    );
  };

  const ANALYSIS_HEAD = (
    <React.Fragment>
      <th className="r">{t('ราคา', 'Price')}</th><th className="r">P/E</th>
      <th>{t('งบไตรมาสล่าสุด', 'latest quarter')}</th><th>{t('เทคนิคอล', 'technical')}</th><th>{t('มุมมอง', 'Read')}</th>
    </React.Fragment>
  );

  return (
    <div className="mm-card glass mm-pd">
      <SecHead th="Parlov Gap รายวัน · หุ้นที่ gap จะลงรอบไหน" en="PARLOV GAP DAILY · which session each gap opens on"
        right={<span className="mono dim">{d.window.from} → {d.window.to} · {d.totalScheduled} {t('รายการ', 'scheduled')}</span>}></SecHead>

      {/* What the study expects in general, so the tables below do not have to
          repeat it on every row. */}
      <div className="mm-pd-ctx">
        <div><span className="mono dim">{t('เกณฑ์ gap ผิดปกติ', 'abnormal gap floor')}</span><b>≥{d.gapFloorPct ?? 3}%</b></div>
        <div><span className="mono dim">{t('gap ไม่ถูกปิดใน 2 สัปดาห์', 'gap NOT filled in 2 weeks')}</span><b className="down">{pct(d.overall.nofillRate, 1)}</b></div>
        {d.overall.gapdown2wk && <div><span className="mono dim">{t('ซื้อ gap-down ถือ 2 สัปดาห์', 'buy gap-down, hold 2wk')}</span><b className="up">{pct(d.overall.gapdown2wk.avg_pct, 2)} · {t('ชนะ', 'win')} {pct(d.overall.gapdown2wk.winrate_pct, 1)}</b></div>}
        {d.overall.gapup2wk && <div><span className="mono dim">{t('ซื้อ gap-up ถือ 2 สัปดาห์', 'buy gap-up, hold 2wk')}</span><b>{pct(d.overall.gapup2wk.avg_pct, 2)} · {t('ชนะ', 'win')} {pct(d.overall.gapup2wk.winrate_pct, 1)}</b></div>}
        {wdBest && <div><span className="mono dim">{t('วันที่ gap แรงสุดในสถิติ', 'strongest gap weekday')}</span><b className="mono">{wdBest.day} {pct(wdBest.avg_abn_gap, 2)}</b></div>}
      </div>

      {/* One table. The gap columns are that company's own 31-year Parlov
          numbers or nothing — the study publishes them for 35 of its 515
          symbols, and a weekday average standing in that column would read as
          the company's own figure when it is not. Covered names sort first. */}
      {/* Pick the session the gap OPENS on. "What gaps tomorrow morning" draws
          from two announcement slots — tonight's after-close reports and
          tomorrow's before-open ones — so the session, not the report date, is
          what a reader is choosing between. */}
      <div className="mm-filterrow" style={{ marginBottom: 10 }}>
        {d.sessions.map(s => (
          <button key={s.date} className={`mm-pill ${session?.date === s.date ? 'on' : ''}`} onClick={() => setGapDate(s.date)}>
            gap {s.weekday} {s.date.slice(5)}
            <em className="dim" style={{ fontStyle: 'normal' }}> · {s.total}</em>
          </button>
        ))}
        {session && session.total > rows.length && (
          <button className="mm-pill" onClick={() => setLimit(l => (l >= 150 ? 60 : 150))}>
            {limit >= 150
              ? t('ย่อกลับ 60 อันดับแรก', 'back to the top 60')
              : `${t('แสดงเพิ่ม', 'show more')} (${t('จาก', 'of')} ${session.total})`}
          </button>
        )}
      </div>

      <h4 className="mm-pd-h">
        {t('gap รอบ', 'Gapping on')} {session?.weekday} {session?.date}
        <em className="dim"> · {t('ประกาศงบทั้งหมด', 'reporting into it')} {session?.total} · {t('แสดง', 'showing')} {rows.length}
          {rows[0]?.weekdayAvgGap != null && <React.Fragment> · {t('ค่าเฉลี่ย gap ของวันนี้ทั้งตลาด', 'all-market average for this weekday')} {pct(rows[0].weekdayAvgGap)}</React.Fragment>}
        </em></h4>
      <div className="mm-pd-scroll">
        <table className="mm-pd-t mono">
          {/* Two header rows: fourteen columns are unreadable as one flat
              strip, but they fall into three obvious groups and the groups are
              what a reader is actually navigating between. */}
          <thead>
            <tr className="mm-pd-grp">
              <th colSpan={2}>{t('ใคร · เมื่อไหร่', 'who · when')}</th>
              <th colSpan={1} className="c">{t('วันนี้', 'today')}</th>
              <th colSpan={4} className="c">{t('สถิติ gap ย้อนหลังของหุ้นนี้', 'this stock’s own gap history')}</th>
              <th colSpan={2} className="c">{t('ตัวเลขคาดการณ์', 'estimates')}</th>
              <th colSpan={5} className="c">{t('ราคา · งบ · เทคนิค · มุมมอง', 'price · filing · technical · read')}</th>
            </tr>
            <tr>
              <th>{t('หุ้น', 'Symbol')}</th><th>{t('ประกาศ', 'Reports')}</th>
              {/* The only sortable column: it is the only one whose ordering a
                  reader wants to change, because it is the only one that is
                  news. Cycles biggest-up, biggest-down, then back to the
                  table's own order. */}
              <th className="r">
                <button className={`mm-pd-sort ${gapSort ? 'on' : ''}`}
                  onClick={() => setGapSort(s => (s === null ? 'desc' : s === 'desc' ? 'asc' : null))}
                  title={t('เรียงตาม Gap วันนี้', 'sort by today’s gap')}>
                  Gap To Day <span className="mm-pd-arrow">{gapSort === 'desc' ? '▼' : gapSort === 'asc' ? '▲' : '⇅'}</span>
                </button>
              </th>
              <th className="r">{t('เฉลี่ย', 'average')}</th><th className="r">{t('สูงสุด', 'max')}</th>
              <th className="r">{t('ไม่ปิดวันนั้น', 'not filled')}</th><th className="r">{t('จำนวนครั้ง', 'events')}</th>
              <th className="r">{t('รายได้', 'revenue')}</th><th className="r">EPS</th>
              {ANALYSIS_HEAD}
            </tr>
          </thead>
          <tbody>
            {ordered.map(r => (
              <tr key={r.symbol}>
                <td>
                  <b>{r.symbol}</b>
                  {r.study?.lists?.length
                    ? <small className="mm-pd-tag">{r.study.lists.map(l => (t(LIST_TH[l], LIST_EN[l]) || l)).join(' · ')}</small>
                    : r.notable ? <small className="mm-pd-tag">{t('หุ้นที่จับตา', 'watched')}</small> : null}
                </td>
                {/* The gap session is the same for every row here — it is what
                    the table was filtered on — so it lives in the heading, not
                    repeated sixty times down a column. */}
                <td>
                  <span className="mono">{r.when}</span>
                  <small className="dim mm-pd-wd">{r.date}</small>
                  {r.reported && <small className="mm-pd-done">{t('ออกแล้ว', 'reported')}</small>}
                </td>
                {(() => {
                  const g = gaps[r.symbol];
                  const wait = <span className="dim mono">{gapsDone ? '—' : '…'}</span>;
                  return (
                    <React.Fragment>
                      {/* The gap that actually printed this session, in its own
                          column: green up, red down, and empty until the bell,
                          because a missing open and a flat open are different
                          facts and must not render the same. */}
                      <td className="r">
                        {(() => {
                          const lg = live[r.symbol];
                          if (!lg || typeof lg.gapPct !== 'number') return <span className="dim mono">—</span>;
                          const up = lg.gapPct >= 0;
                          return <span className={`mm-pd-gap ${up ? 'up' : 'down'}`}>{up ? '+' : ''}{lg.gapPct.toFixed(2)}%</span>;
                        })()}
                        {/* Live, and only while the session it describes is the
                            one running. */}
                        {typeof nowChg[r.symbol] === 'number' && (
                          <small className={`mm-pd-now ${nowChg[r.symbol] >= 0 ? 'up' : 'down'}`}>
                            {t('ตอนนี้', 'now')} {nowChg[r.symbol] >= 0 ? '+' : ''}{nowChg[r.symbol].toFixed(2)}%
                          </small>
                        )}
                      </td>
                      <td className="r">
                        {/* A mean over a handful of events should not read with
                            the same confidence as one over four hundred, so a
                            thin sample loses the colour. `n` sits two columns
                            over, but nobody scanning an average looks at it. */}
                        {g?.avgAbnGap != null
                          ? <span className={g.n != null && g.n < 20 ? 'dim' : 'up'}
                              title={g.n != null && g.n < 20 ? t('ตัวอย่างน้อย — เชื่อถือได้จำกัด', 'few events — treat with care') : undefined}>
                              {pct(g.avgAbnGap)}{g.n != null && g.n < 20 ? '*' : ''}
                            </span>
                          : wait}
                        {r.study?.avgAbnGap != null && <small className="mm-pd-wd dim">Parlov {pct(r.study.avgAbnGap)}</small>}
                      </td>
                      <td className="r dim">
                        {g?.maxGap != null ? pct(g.maxGap) : wait}
                        {g?.maxAt && <small className="mm-pd-wd dim">{g.maxAt}</small>}
                      </td>
                      <td className="r">
                        {g?.nofillRate != null ? <span className="down">{pct(g.nofillRate, 0)}</span> : wait}
                      </td>
                      <td className="r dim">
                        {g?.n != null && g.n > 0 ? g.n : wait}
                        {g?.from && <small className="mm-pd-wd dim">{t('ตั้งแต่', 'since')} {g.from.slice(0, 4)}</small>}
                      </td>
                    </React.Fragment>
                  );
                })()}
                <td className="r dim">{r.revEst}</td>
                <td className="r">{r.est}</td>
                <AnalysisCells sym={r.symbol}></AnalysisCells>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Five paragraphs of method under a table is five paragraphs nobody
          reads. One line stays visible; the rest opens on request. */}
      <button className="mm-pd-howbtn" onClick={() => setHow(v => !v)}>
        {how ? '▾' : '▸'} {t('วิธีอ่านตาราง และที่มาของตัวเลข', 'How to read this, and where the numbers come from')}
      </button>
      {how && (
        <div className="mm-pd-how">
          <p><b>{t('Gap To Day กับบรรทัด "ตอนนี้"', 'Gap To Day and the "now" line')}</b>
            {t(' Gap To Day คือ gap ตอนเปิด = ราคาเปิดเทียบราคาปิดวันก่อน ซึ่งถูกล็อกทันทีที่ตลาดเปิดและไม่ขยับอีกทั้งวัน — เป็นตัวเลขที่งานวิจัยใช้ · บรรทัด "ตอนนี้" ใต้มันคือราคาปัจจุบันเทียบราคาปิดวันก่อน อัปเดตทุก 20 วินาที และแสดงเฉพาะรอบที่กำลังเกิดขึ้นวันนี้เท่านั้น',
               ' Gap To Day is the OPENING gap - the open against the previous close - which is fixed the moment the session opens and does not move again all day; it is the figure the study is built on. The "now" line beneath it is the current price against that same previous close, refreshed every 20 seconds, and it appears only for the session running today.')}</p>
          <p><b>{t('gap ลงวันไหน', 'Which session the gap lands on')}</b>
            {t(' ก่อนเปิด (BMO) gap ลงวันเดียวกัน · หลังปิด (AMC) gap ลงรอบถัดไป ข้ามเสาร์–อาทิตย์ให้แล้ว ยังไม่รวมวันหยุดตลาด',
               ' BMO gaps the same session; AMC gaps the next one. Weekends are skipped; market holidays are not modelled.')}</p>
          <p><b>{t('สถิติ gap ย้อนหลัง', 'The gap history')}</b>
            {t(' คำนวณเองจากแท่งรายวันทุกแท่งที่ Yahoo มีของหุ้นตัวนั้น ลึกสุดเท่าที่ข้อมูลมีจริง (CAT ถึงปี 1970 · PLTR ถึง 2020 เพราะเพิ่งเข้าตลาด) นับเฉพาะ gap ผิดปกติ ≥3% เท่าเกณฑ์งานวิจัย · "ไม่ปิดวันนั้น" คือทั้งวันราคาไม่เคยวิ่งกลับไปแตะราคาปิดวันก่อน ซึ่งเข้มกว่าเกณฑ์ 4 ชม. ของงานวิจัย',
               ' Computed here from every daily bar Yahoo holds for that symbol, as deep as the data goes (CAT reaches 1970; PLTR only 2020, its listing year). Only abnormal gaps ≥3% count, the study’s own floor. “Not filled” means the session never traded back to the previous close — stricter than the study’s four-hour test.')}</p>
          <p><b>{t('ต่างจากงานวิจัยตรงไหน', 'How it differs from the study')}</b>
            {t(' เรานับ gap ผิดปกติทุกสาเหตุ ไม่ใช่เฉพาะวันงบ เพราะวันงบย้อนหลังลึก ๆ หาไม่ได้บนชุดข้อมูลนี้ (Finnhub ฟรีให้ 4 ไตรมาส) · เทียบกับ 25 ตัวที่งานวิจัยตีพิมพ์เลขไว้ ค่าของเรา ต่ำกว่าเฉลี่ยราว 0.9 จุด และต่ำกว่า 23 จาก 25 ตัว — ไปในทางที่ควรเป็น เพราะวันประกาศงบคือวันที่ gap แรงที่สุด พอเรานับวันอื่นเข้ามาด้วย ค่าเฉลี่ยจึงถูกดึงลง (BKNG เรา 6.38% งานวิจัย 6.68% · ต่างมากสุดคือ TTD 1.76 จุด) · ตัวไหนงานวิจัยมีเลข จะพิมพ์ไว้ใต้ค่าของเรา · เลขที่มี * คือมีเหตุการณ์น้อยกว่า 20 ครั้ง ให้ระวัง',
               ' We count abnormal gaps from any cause, not only earnings days — earnings dates that far back are not obtainable here (Finnhub’s free tier gives four quarters). Against the 25 names the study publishes figures for, ours runs about 0.9 points lower and is lower on 23 of the 25 — the direction it should be, since earnings days are a stock’s biggest gaps and counting the other days pulls the mean down (BKNG reads 6.38% here against 6.68%; the widest gap is TTD at 1.76 points). The study’s figure is printed under ours, and a value marked * rests on fewer than 20 events.')}</p>
          <p><b>{t('ใครได้ขึ้นตาราง', 'Who makes the table')}</b>
            {t(' แต่ละรอบมีหุ้นประกาศงบนับร้อย จึงคัดมาเท่าที่อ่านไหวโดยดูขนาดรายได้ (เรียงตามรายได้ล้วน ๆ จะดันชื่อที่คนรออย่าง PLTR ตกไปแถวหกสิบกว่า จึงยกตัวที่งานวิจัยมีสถิติ และหุ้นที่จับตาจากฟีด Featured/Quant Watchlist ของ nopoly ขึ้นก่อน) · ส่วนลำดับที่เห็นในตาราง หลังกลุ่มหุ้นที่จับตาแล้ว จะเรียงตามคะแนนงบไตรมาสล่าสุด ดีสุดลงไปแย่สุด ตัวที่ไม่มีงบอยู่ท้ายสุด',
               ' Each session has hundreds of reports, so the table keeps as many as can be read, chosen by size — with study-covered names and the desk’s watched names (nopoly’s Featured and Quant Watchlist feeds) lifted first, because revenue alone buries names people wait for, like PLTR. The order you see, after the watched group, runs by latest-quarter score from best to worst, with no-filing names last.')}</p>
          <p><b>{t('มุมมอง ซื้อ/ถือ/ขาย', 'The read')}</b>
            {t(' เกณฑ์เดียวกับตารางในงานวิจัยด้านล่าง ให้คะแนนสองด้านแยกกัน — ซื้อ ต้องได้ทั้งเทคนิค ≥+2 และงบ ≥+2 · ขาย ต้องเสียทั้งสองด้าน · ขาดด้านใดด้านหนึ่งไม่ขึ้นซื้อ · ราคาและงบจาก Yahoo หุ้นที่ไม่มีข้อมูลจะขึ้น —',
               ' Same rules as the study’s table below, scored on two sides separately: BUY needs technical ≥+2 AND fundamentals ≥+2, SELL needs both against, and a missing side never reads BUY. Prices and filings via Yahoo; names with no coverage show —.')}</p>
          {d.stale && <p className="dim">{t('ข้อมูลงานวิจัยมาจากสแนปช็อตล่าสุด (ต้นทางไม่ตอบ)', 'Study data served from the last snapshot — upstream unreachable.')}</p>}
        </div>
      )}
    </div>
  );
}
